import { ChevronDown, Loader2, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AdminApi } from "@/api";
import {
  Banner,
  Button,
  Card,
  EmptyState,
  SelectField,
  TextField,
} from "@/components/ui";
import type {
  EngagementEnvironment,
  ProactiveLogDetail,
  ProactiveLogItem,
} from "@/types";
import { ProactiveLogModal } from "./ProactiveLogModal";
import { ProactiveLogRow } from "./ProactiveLogRow";
import { PROACTIVE_TABLE_COLUMNS } from "./proactiveTable";

const PAGE_SIZE = 30;
const ENVIRONMENTS: EngagementEnvironment[] = ["dev", "prod"];

type Cursors = Record<EngagementEnvironment, string | null>;

function rowKey(item: ProactiveLogItem) {
  return `${item._environment}:${item.message_id}`;
}

/** Newest first across both environments. */
function sortItems(items: ProactiveLogItem[]) {
  return [...items].sort((a, b) => b.created_at.localeCompare(a.created_at));
}

function dedupe(items: ProactiveLogItem[]) {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = rowKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function ProactiveLogPanel({ api }: { api: AdminApi }) {
  const [items, setItems] = useState<ProactiveLogItem[]>([]);
  const [cursors, setCursors] = useState<Cursors>({ dev: null, prod: null });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState<string[]>([]);

  const [kind, setKind] = useState("all");
  const [email, setEmail] = useState("");
  const [sinceDays, setSinceDays] = useState("14");

  const [selectedKey, setSelectedKey] = useState("");
  const [detail, setDetail] = useState<ProactiveLogDetail | undefined>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const selected = items.find((item) => rowKey(item) === selectedKey);
  const hasMore = Boolean(cursors.dev || cursors.prod);

  const query = useMemo(
    () => ({
      kind: kind as "all" | "auto" | "manual",
      email: email.trim() || undefined,
      sinceDays: Number(sinceDays) || 14,
    }),
    [kind, email, sinceDays],
  );

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      // Both environments in parallel; each keeps its own cursor because the
      // two datasets page independently.
      // Prod only for an admin with prod access (SAMS says which).
      const environments = (await api.readableEnvironments()).filter((e) => ENVIRONMENTS.includes(e));
      const pages = await Promise.all(
        environments.map((environment) =>
          api
            .listProactiveLog({ environment, limit: PAGE_SIZE, ...query })
            .then((page) => ({ environment, page })),
        ),
      );
      setItems(sortItems(pages.flatMap((p) => p.page.items || [])));
      setCursors({
        dev: pages.find((p) => p.environment === "dev")?.page.next_cursor ?? null,
        prod: pages.find((p) => p.environment === "prod")?.page.next_cursor ?? null,
      });
      // A prod failure is a note, not an error — dev rows are still usable.
      setNotes(pages.map((p) => p.page.load_error).filter(Boolean) as string[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    const pending = ENVIRONMENTS.filter((environment) => cursors[environment]);
    if (pending.length === 0) return;
    setLoadingMore(true);
    setError("");
    try {
      const pages = await Promise.all(
        pending.map((environment) =>
          api
            .listProactiveLog({
              environment,
              limit: PAGE_SIZE,
              cursor: cursors[environment],
              ...query,
            })
            .then((page) => ({ environment, page })),
        ),
      );
      setItems((current) =>
        sortItems(dedupe([...current, ...pages.flatMap((p) => p.page.items || [])])),
      );
      setCursors((current) => {
        const next = { ...current };
        for (const p of pages) next[p.environment] = p.page.next_cursor ?? null;
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (item: ProactiveLogItem) => {
    setSelectedKey(rowKey(item));
    setDetail(undefined);
    setDetailError("");
    setDetailLoading(true);
    try {
      setDetail(
        await api.getProactiveLogDetail(item._environment, item.message_id),
      );
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : String(err));
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="grid gap-4">
      {/* The two things most likely to be misread off this table. */}
      <Banner tone="success">
        <strong>미관측</strong>은 반응이 없었다는 뜻이 아니라 관측되지 않았다는 뜻입니다.{" "}
        <strong>전달 안 됨</strong>은 사용자 반응이 아니라 전송 실패입니다 — 콘텐츠 품질
        판단에 쓰지 마세요. 생성 프롬프트는 서버 코드에 있어 이 화면이나 Prompt Lab에서
        편집할 수 없습니다. 개선이 필요하면 상세에서 <strong>사례 복사</strong>로
        개발팀에 전달해 주세요.
      </Banner>

      {error && <Banner tone="error">{error}</Banner>}
      {notes.map((note) => (
        <Banner key={note} tone="error">
          {note} (다른 환경 결과는 그대로 표시됩니다)
        </Banner>
      ))}

      <Card className="grid gap-3 p-4 md:grid-cols-[160px_1fr_140px_auto]">
        <SelectField
          label="유형"
          value={kind}
          onChange={setKind}
          options={["all", "auto", "manual"]}
        />
        <TextField
          label="수신자 이메일"
          value={email}
          onChange={setEmail}
          placeholder="비우면 전체"
        />
        <SelectField
          label="기간(일)"
          value={sinceDays}
          onChange={setSinceDays}
          options={["7", "14", "30", "90"]}
        />
        <Button className="self-end" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          조회
        </Button>
      </Card>

      {items.length > 0 && (
        <div
          className={`${PROACTIVE_TABLE_COLUMNS} px-4.5 text-xs font-medium text-muted-foreground`}
        >
          <span>발송시각</span>
          <span>유형</span>
          <span>수신자</span>
          <span>세션</span>
          <span>내용</span>
          <span>반응</span>
          <span>env</span>
        </div>
      )}

      <div className="grid gap-2">
        {items.map((item) => (
          <ProactiveLogRow
            key={rowKey(item)}
            item={item}
            selected={rowKey(item) === selectedKey}
            onSelect={() => void openDetail(item)}
          />
        ))}
      </div>

      {!loading && items.length === 0 && (
        <EmptyState
          title="발송 기록이 없습니다"
          body="기간을 늘리거나 필터를 비워보세요. prod DB가 설정되지 않은 환경에서는 dev 결과만 표시됩니다."
        />
      )}

      {hasMore && (
        <div className="flex justify-center pt-2 pb-4.5">
          <Button
            onClick={loadMore}
            disabled={loading || loadingMore}
            variant="outline"
          >
            {loadingMore ? <Loader2 className="animate-spin" /> : <ChevronDown />}
            Load more
          </Button>
        </div>
      )}

      <ProactiveLogModal
        item={selected}
        detail={detail}
        loading={detailLoading}
        error={detailError}
        open={Boolean(selected)}
        onClose={() => setSelectedKey("")}
      />
    </div>
  );
}
